import {
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  SxProps,
} from '@mui/material';
import { ArrowDropDown, AddCircle, Search } from '@mui/icons-material';
import {
  useCallback,
  useMemo,
  SyntheticEvent,
  useState,
  MouseEvent,
  useEffect,
  ReactNode,
  useRef,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  AddFiltersButton,
  AdditionalFilters,
  ButtonLabel,
  FilterAccordian,
  FilterButton,
  FilterCheckBox,
  FilterChip,
  FilterDetails,
  FilterFormControlLabel,
  FilterFormLabel,
  FilterMenu,
  FilterMenuListProps,
  FilterTextField,
  FiltersCount,
  ToolTipProps,
  searchStyles,
} from './styles';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  TemplateField,
  useAllPaintRegionsForFilterAndSearchPanelQuery,
  PoiAccessibilityEnum,
  usePaintRegionGroupsQuery,
  useDecksForDeckListQuery,
} from '@/__generated__/graphql';
import { formatEnumForDisplay, toKebabCase } from '@/utils';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';
import { NOT_APPLICABLE_LABEL } from '@/constants';

import type { CustomFilterConfig } from '@/hooks/useCustomFilters';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks';
import { allPaintRegionGroups, selectedPaintRegionGroupId } from '@/components/Analysis/state';
import { useSetAtom } from 'jotai';
import { toSnakeCase } from '@/utils/string';

type PartialTemplateField = Partial<TemplateField>;

export type SelectedValues = {
  [fieldId: string]: string[];
};

type ToggleFilterConfig = {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

type FilterAndSearchPanelProps = {
  templateFields: Array<PartialTemplateField>;
  selectedValues: SelectedValues;
  setSelectedValues: (selectedValues: SelectedValues) => void;
  handleSelectedChange: (values: SelectedValues) => void;
  showPaintRegions?: boolean;
  searchBar?: boolean;
  searchTerm?: string | undefined;
  searchPlaceholder?: string | undefined;
  setSearch?: (value: string) => void;
  filterOptions?: boolean;
  endAdornment?: ReactNode;
  customFiltersConfig?: CustomFilterConfig[];
  customFilterValues?: Record<string, string[]>;
  handleCustomFilterChange?: (key: string, values: string[]) => void;
  sx?: SxProps;
  hideStaticFilters?: boolean;
  location?: string;
  toggleFilters?: ToggleFilterConfig[];
  savedFilterKeys?: string[] | null;
  onFilterKeysChange?: (keys: string[]) => void;
  isFiltersPersistenceInitialized?: boolean;
  filterPersistenceKey?: string;
  poiType?: string;
};

type TemplateFieldFilterProps = {
  field: PartialTemplateField | { name: string; options: string[] };
  selected: string[];
  handleFilterValuesChange: (values: string[]) => void;
  location?: string;
};

type StaticFiltersType = { id: string; name: string; options: Array<string> };

type FilterMenuProps = {
  items: Partial<TemplateField>[];
  selected: Partial<TemplateField>[];
  setSelected: (loading: Partial<TemplateField>[]) => void;
  staticFilters?: Array<StaticFiltersType>;
};

const StaticFilters = {
  verification: ['Pending', 'Verified'],
  accessibility: Object.values(PoiAccessibilityEnum).map((accessibility) =>
    formatEnumForDisplay(accessibility)
  ),
  newlyCreatedPoi: ['Yes', 'No'],
  unAssignedPoi: ['Yes', 'No'],
  mediaAttachment: ['Yes', 'No'],
  governing: ['Yes', 'No'],
};

const AddFilterMenu = ({ items, staticFilters, selected, setSelected }: FilterMenuProps) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>(undefined);
  const open = Boolean(anchorElement);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const updateSelected = (item: Partial<TemplateField>) => {
    const index = selected.findIndex((field) => field.key === item.key);
    setSelected(
      index === -1 ? [...selected, item] : selected.filter((field) => field.key !== item.key)
    );
  };

  return (
    <>
      <AddFiltersButton onClick={handleClick}>
        <AddCircle />
      </AddFiltersButton>
      <FilterMenu
        anchorEl={anchorElement}
        open={open}
        onClose={handleClose}
        MenuListProps={FilterMenuListProps}
      >
        {items.map((item) => (
          <FilterFormControlLabel
            key={item.key}
            value={item.key}
            control={
              <FilterCheckBox
                checked={Boolean(selected.some((field) => field.key === item.key))}
                onChange={() => {
                  updateSelected(item);
                }}
              />
            }
            label={item.name}
          />
        ))}
        {staticFilters?.map((item) => (
          <FilterFormControlLabel
            key={item.id}
            value={item.id}
            control={
              <FilterCheckBox
                checked={Boolean(selected.some((field) => field.id === item.id))}
                onChange={() => {
                  updateSelected(item);
                }}
              />
            }
            label={item.name}
          />
        ))}
      </FilterMenu>
    </>
  );
};

const TemplateFieldFilter = ({
  field,
  selected,
  handleFilterValuesChange,
  location = '',
}: TemplateFieldFilterProps) => {
  const options = useMemo(() => {
    const fieldOptions = field.options || [];
    return fieldOptions.map((option) => ({ label: option, value: option }));
  }, [field.options]);

  const selectedOptions = useMemo(() => {
    return selected.map((selectedValue) => {
      const option = options.find((opt) => opt.value === selectedValue);
      return { label: option?.label || selectedValue, value: selectedValue };
    });
  }, [selected, options]);

  const handleSelectedOptionChange = useCallback(
    (event: SyntheticEvent<Element, Event>, values: { label: string; value: string }[]) => {
      handleFilterValuesChange(values.map((valueItem) => valueItem.value));
    },
    [handleFilterValuesChange]
  );

  return (
    <Box sx={{ marginTop: '-6px' }}>
      <FilterFormLabel>{field.name}</FilterFormLabel>
      <Autocomplete
        id={`${location}-${toKebabCase(field.name)}-filter`}
        multiple
        disableCloseOnSelect
        data-testid={`filter-field-${field.name}`}
        size="small"
        fullWidth={false}
        options={options.filter((option) => option.value)}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={selectedOptions}
        getOptionLabel={(option) => option.label}
        onChange={handleSelectedOptionChange}
        noOptionsText="No Options"
        disableClearable
        renderTags={(value, getTagProps) => {
          const chipLimit = 2;
          const additionalValues =
            value.length > chipLimit
              ? value
                  .slice(chipLimit)
                  .map((item) => item.label)
                  .join(', ')
              : '';
          return (
            <>
              {value.slice(0, chipLimit).map((option, index) => (
                <FilterChip {...getTagProps({ index })} label={option.label} key={option.label} />
              ))}
              <Tooltip
                title={additionalValues}
                arrow
                placement="bottom"
                componentsProps={ToolTipProps}
              >
                <AdditionalFilters>
                  {value.length > chipLimit && `+ ${value.length - chipLimit}`}
                </AdditionalFilters>
              </Tooltip>
            </>
          );
        }}
        sx={{
          minWidth: '157px',
          background: '#F6F8FB',
          '& .MuiAutocomplete-tag': {
            marginRight: '12px',
            marginLeft: '-8px',
          },
          '& fieldset': {
            border: '1px solid #B8CADD !important',
          },
        }}
        renderInput={(parameters) => (
          <FilterTextField
            {...parameters}
            placeholder={selectedOptions.length === 0 ? `Select ${field.name}` : ''}
            InputProps={{
              ...parameters.InputProps,
              sx: {
                '&::placeholder': { fontSize: '0.7rem' },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

type FilterButtonProps = {
  expand: boolean;
  onClick: () => void;
  filterCount: number;
  validTemplateFields: Partial<TemplateField>[];
  staticFilters?: Array<StaticFiltersType>;
  location?: string;
};

const DropDownFilterButton = ({
  expand,
  onClick,
  filterCount,
  validTemplateFields,
  staticFilters,
  location,
}: FilterButtonProps) => {
  return (
    <FilterButton
      id={`${location}-filter-button`}
      data-testid="panel-filter-button"
      onClick={onClick}
      disabled={validTemplateFields.length === 0 && staticFilters?.length === 0}
    >
      <FilterDetails>
        <Stack direction="row" alignItems="center">
          <ButtonLabel>Filters</ButtonLabel>
          <FiltersCount>{filterCount}</FiltersCount>
        </Stack>
        <ArrowDropDown
          sx={{
            fontSize: '2rem',
            transform: `rotate(${expand ? '180deg' : '0deg'})`,
          }}
        />
      </FilterDetails>
    </FilterButton>
  );
};

export const FilterAndSearchPanel = ({
  templateFields,
  selectedValues,
  setSelectedValues,
  handleSelectedChange,
  showPaintRegions,
  searchBar,
  searchTerm = '',
  searchPlaceholder,
  setSearch = () => {},
  filterOptions = true,
  endAdornment,
  customFiltersConfig = [],
  customFilterValues = {},
  handleCustomFilterChange = () => {},
  sx = {},
  hideStaticFilters = false,
  location = 'insights',
  toggleFilters = [],
  savedFilterKeys,
  onFilterKeysChange,
  isFiltersPersistenceInitialized = false,
  filterPersistenceKey,
  poiType,
}: FilterAndSearchPanelProps) => {
  const { getStatusOptions } = usePoiStatusLabels();
  const structureId = useGetStructureId();
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);
  const setAllPaintRegionGroups = useSetAtom(allPaintRegionGroups);
  const [validTemplateFields, setValidTemplateFields] = useState<Partial<TemplateField>[]>(
    templateFields.filter((field) => field.key !== undefined)
  );
  const setSelectedPaintRegionGroupId = useSetAtom(selectedPaintRegionGroupId);

  useEffect(() => {
    setValidTemplateFields(templateFields.filter((field) => field.key !== undefined));
  }, [templateFields]);

  const [selectedFields, setSelectedFields] = useState<Partial<TemplateField>[]>(
    validTemplateFields.slice(0, 4) || []
  );
  const autoAddedFieldsRef = useRef<Set<string>>(new Set());
  const hasInitializedFromDbRef = useRef(false);
  const prevFilterPersistenceKeyRef = useRef<string | undefined>(undefined);
  const lastNotifiedKeysRef = useRef<string>('');
  const isInitializingRef = useRef(false);

  // Reset on persistence key change
  useEffect(() => {
    if (
      prevFilterPersistenceKeyRef.current !== undefined &&
      prevFilterPersistenceKeyRef.current !== filterPersistenceKey
    ) {
      hasInitializedFromDbRef.current = false;
      lastNotifiedKeysRef.current = '';
    }
    prevFilterPersistenceKeyRef.current = filterPersistenceKey;
  }, [filterPersistenceKey]);

  // Notify parent of filter changes (user-initiated only)
  useEffect(() => {
    if (!onFilterKeysChange || !hasInitializedFromDbRef.current || isInitializingRef.current)
      return;

    const currentKeys = selectedFields.map((f) => f.key).filter(Boolean) as string[];

    const currentKeysStr = currentKeys.sort().join(',');

    if (currentKeys.length > 0 && currentKeysStr !== lastNotifiedKeysRef.current) {
      lastNotifiedKeysRef.current = currentKeysStr;
      onFilterKeysChange(currentKeys);
    }
  }, [selectedFields, onFilterKeysChange]);

  // Auto-add fields with values from URL restoration
  useEffect(() => {
    if (!validTemplateFields.length) return;
    if (Object.keys(selectedValues).length === 0) return;

    // Find fields with values not yet in selectedFields
    const fieldsToAdd: Partial<TemplateField>[] = [];

    Object.keys(selectedValues).forEach((key) => {
      if (
        [
          'isGoverning',
          'isVerified',
          'accessibility',
          'status',
          'isManuallyCreated',
          'unAssignedPoi',
          'mediaAttachment',
          'deck',
        ].includes(key)
      ) {
        return;
      }

      // Skip if already auto-added
      if (autoAddedFieldsRef.current.has(key)) {
        return;
      }

      // Find the field definition in validTemplateFields
      const fieldDef = validTemplateFields.find((field) => field.key === key);
      if (fieldDef) {
        fieldsToAdd.push(fieldDef);
      }
    });

    if (fieldsToAdd.length > 0) {
      // Mark these fields as auto-added
      fieldsToAdd.forEach((field) => {
        if (field.key) {
          autoAddedFieldsRef.current.add(field.key);
        }
      });

      setSelectedFields((prev) => {
        // Only add fields that aren't already in the list
        const newFields = fieldsToAdd.filter(
          (field) => !prev.some((existingField) => existingField.key === field.key)
        );
        return [...prev, ...newFields];
      });
    }
  }, [selectedValues, validTemplateFields]);

  const { data: paintRegionsData } = useAllPaintRegionsForFilterAndSearchPanelQuery({
    variables: {
      input: {
        structureId,
      },
    },
    fetchPolicy: 'cache-first',
  });

  const { data: paintRegionGroupsData } = usePaintRegionGroupsQuery({
    variables: {
      input: {
        structureId,
        templateId: selectedAssemblyTemplateId || '',
      },
    },
    skip: !selectedAssemblyTemplateId || !structureId,
    fetchPolicy: 'cache-first',
    onCompleted: (response) => {
      setAllPaintRegionGroups(response?.paintRegionGroups || []);
      const defaultPaintGroupId = response?.paintRegionGroups?.find((item) => item.isDefault)?.id;
      setSelectedPaintRegionGroupId(defaultPaintGroupId);
    },
  });

  const { data: deckList } = useDecksForDeckListQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const selectedFieldIds = new Set(selectedFields.map((field) => field.key));
    const updatedValues = { ...selectedValues };
    Object.keys(selectedValues).forEach((key) => {
      if (!selectedFieldIds.has(key)) {
        delete updatedValues[key];
      }
    });
    setSelectedValues(updatedValues);
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFields]);

  // Auto-expand filters when values are restored from URL
  useEffect(() => {
    const hasFilterValues = Object.keys(selectedValues).length > 0;
    if (hasFilterValues && !filtersExpanded) {
      setFiltersExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paintRegionOptions = useMemo(() => {
    if (
      paintRegionGroupsData?.paintRegionGroups &&
      paintRegionGroupsData?.paintRegionGroups?.length < 2
    ) {
      return [
        {
          name: 'Paint Block',
          key: 'paint_block',
          options: [
            ...(paintRegionsData?.allPaintRegions.map((paintRegion) => paintRegion.name) ?? []),
            NOT_APPLICABLE_LABEL,
          ],
        },
      ];
    }
    return paintRegionGroupsData?.paintRegionGroups.map((group) => {
      return {
        name: group.name,
        key: group.name === 'Paint Blocks' ? 'paint_block' : toSnakeCase(group.name),
        options: [
          ...(paintRegionsData?.allPaintRegions
            .filter((paintRegion) => paintRegion.paintRegionGroupId === group.id)
            .map((paintRegion) => paintRegion.name) ?? []),
          NOT_APPLICABLE_LABEL,
        ],
      };
    });
  }, [paintRegionGroupsData, paintRegionsData?.allPaintRegions]);

  const deckOptions = useMemo(() => {
    return [...(deckList?.allDecks?.map((deck) => deck.name) ?? []), NOT_APPLICABLE_LABEL];
  }, [deckList?.allDecks]);

  const poiStatusOptions = useMemo(() => {
    return getStatusOptions(poiType);
  }, [getStatusOptions, poiType]);

  const handleFilterValuesChange = useCallback(
    (fieldKey: string) => (values: string[]) => {
      handleSelectedChange({
        ...selectedValues,
        [fieldKey]: values,
      });
    },
    [handleSelectedChange, selectedValues]
  );

  const handleDeckChange = useCallback(
    (values: string[]) => {
      handleSelectedChange({
        ...selectedValues,
        deck: values,
      });
    },
    [handleSelectedChange, selectedValues]
  );

  const handlePaintBlockChange = useCallback(
    (fieldKey: string) => (values: string[]) => {
      handleSelectedChange({
        ...selectedValues,
        [fieldKey]: values,
      });
    },
    [handleSelectedChange, selectedValues]
  );

  const verificationOptions = useMemo(() => {
    return {
      id: 'isVerified',
      key: 'isVerified',
      name: 'Verification',
      options: StaticFilters.verification,
    };
  }, []);

  const governingOptions = useMemo(() => {
    return {
      id: 'isGoverning',
      key: 'isGoverning',
      name: 'Governing',
      options: StaticFilters.governing,
    };
  }, []);

  const accessibilityOptions = useMemo(() => {
    return {
      id: 'accessibility',
      key: 'accessibility',
      name: 'Accessibility',
      options: StaticFilters.accessibility,
    };
  }, []);

  const statusOptions = useMemo(() => {
    return {
      id: 'status',
      key: 'status',
      name: 'Status',
      options: poiStatusOptions.map((opt) => opt.label),
    };
  }, [poiStatusOptions]);

  const newlyCreatedPoi = useMemo(() => {
    return {
      id: 'isManuallyCreated',
      key: 'isManuallyCreated',
      name: 'Imported POI',
      options: StaticFilters.newlyCreatedPoi,
    };
  }, []);

  const unAssignedPoi = useMemo(() => {
    return {
      id: 'unAssignedPoi',
      key: 'unAssignedPoi',
      name: '3D UnAssigned POI',
      options: StaticFilters.unAssignedPoi,
    };
  }, []);

  const mediaAttachment = useMemo(() => {
    return {
      id: 'mediaAttachment',
      key: 'mediaAttachment',
      name: 'Media Attachment',
      options: StaticFilters.mediaAttachment,
    };
  }, []);

  const staticFilters = useMemo(() => {
    if (hideStaticFilters) {
      return [];
    }
    const baseStaticFilters = [
      governingOptions,
      verificationOptions,
      accessibilityOptions,
      newlyCreatedPoi,
      unAssignedPoi,
      mediaAttachment,
    ];

    if (poiType) {
      baseStaticFilters.push(statusOptions);
    }

    return baseStaticFilters;
  }, [
    hideStaticFilters,
    governingOptions,
    verificationOptions,
    accessibilityOptions,
    statusOptions,
    newlyCreatedPoi,
    unAssignedPoi,
    mediaAttachment,
    poiType,
  ]);

  // Initialize filters from saved state or defaults
  useEffect(() => {
    if (!validTemplateFields.length) return;
    if (hasInitializedFromDbRef.current) return;
    if (onFilterKeysChange && !isFiltersPersistenceInitialized) return;

    isInitializingRef.current = true;

    const allAvailableFilters = [...validTemplateFields, ...staticFilters];
    const defaultFields =
      validTemplateFields.length < 4
        ? [...validTemplateFields, ...staticFilters]
        : validTemplateFields.slice(0, 4);
    const defaultKeys = defaultFields.map((f) => f.key).filter(Boolean) as string[];

    if (Array.isArray(savedFilterKeys) && savedFilterKeys.length > 0) {
      // Restore from saved
      const restoredFields = savedFilterKeys
        .map((key: string) => allAvailableFilters.find((f) => f.key === key))
        .filter(Boolean) as Partial<TemplateField>[];

      if (restoredFields.length > 0) {
        setSelectedFields(restoredFields);
        lastNotifiedKeysRef.current = savedFilterKeys.slice().sort().join(',');
      } else {
        // Saved keys invalid, use defaults without persisting
        setSelectedFields(defaultFields);
        lastNotifiedKeysRef.current = defaultKeys.sort().join(',');
      }
    } else if (onFilterKeysChange) {
      // No saved filters, persist defaults
      setSelectedFields(defaultFields);
      lastNotifiedKeysRef.current = defaultKeys.sort().join(',');
      onFilterKeysChange(defaultKeys);
    } else {
      // Non-persisted mode
      setSelectedFields(defaultFields);
    }

    hasInitializedFromDbRef.current = true;

    // Defer clearing flag to allow React to batch state updates
    setTimeout(() => {
      isInitializingRef.current = false;
    }, 0);
  }, [
    validTemplateFields,
    staticFilters,
    savedFilterKeys,
    isFiltersPersistenceInitialized,
    onFilterKeysChange,
  ]);

  return (
    <Box sx={{ marginBottom: '2.4rem', ...sx }}>
      <Stack direction="row" alignItems="center" gap={2} justifyContent="space-between">
        {filterOptions && (
          <DropDownFilterButton
            onClick={() => {
              setFiltersExpanded(!filtersExpanded);
            }}
            expand={filtersExpanded}
            filterCount={
              Object.values(selectedValues).filter((v) => (Array.isArray(v) ? v.length > 0 : !!v))
                .length + (toggleFilters?.filter((toggle) => toggle.checked).length || 0)
            }
            validTemplateFields={validTemplateFields}
            staticFilters={staticFilters}
            location={location}
          />
        )}
        {searchBar && (
          <TextField
            fullWidth
            data-testid="panel-search-input"
            id={`${location}-search-input`}
            size="small"
            sx={searchStyles}
            placeholder={searchPlaceholder ?? 'Search'}
            value={searchTerm}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <Search htmlColor="#6E8198" sx={{ marginRight: '8px' }} />,
              endAdornment: searchTerm.length > 0 && (
                <InputAdornment position="end">
                  <CloseIcon sx={{ cursor: 'pointer' }} onClick={() => setSearch('')} />
                </InputAdornment>
              ),
            }}
          />
        )}
        {endAdornment && endAdornment}
      </Stack>
      {filterOptions && (
        <FilterAccordian expanded={filtersExpanded} square>
          <AccordionSummary
            id="panel-header"
            aria-controls="panel-content"
            sx={{ display: 'none' }}
          />
          <AccordionDetails sx={{ padding: '12px 16px' }}>
            <Stack
              direction="row"
              sx={{
                flexWrap: 'wrap',
                gap: 2,
              }}
              alignItems="flex-end"
              id={`${location}-filter-panel`}
            >
              {showPaintRegions &&
                paintRegionOptions?.map((group) => (
                  <TemplateFieldFilter
                    key={group.name}
                    field={group}
                    location={location}
                    selected={selectedValues[group.key] ?? []}
                    handleFilterValuesChange={handlePaintBlockChange(group.key)}
                  />
                ))}

              {/* Custom Filters rendering */}
              {customFiltersConfig && customFiltersConfig.length > 0 && (
                <>
                  {customFiltersConfig.map((filter) => {
                    const currentSelectedValues = customFilterValues[filter.key] || [];
                    // Find the corresponding labels for the selected values in the current unit system
                    const selectedLabels = currentSelectedValues
                      .map((selectedValue) => {
                        // Find the option that matches the selected value
                        const matchingOption = filter.options.find(
                          (opt) => String(opt.value) === selectedValue
                        );
                        return matchingOption?.label || selectedValue;
                      })
                      .filter(Boolean);

                    return (
                      <TemplateFieldFilter
                        key={`${filter.key}-${filter.options[0]?.label}`}
                        field={{
                          name: filter.label,
                          options: filter.options.map((opt) => opt.label ?? ''),
                        }}
                        selected={selectedLabels}
                        location={location}
                        handleFilterValuesChange={(values) => {
                          // Map selected labels back to values
                          const mapped = filter.options
                            .filter(
                              (opt) =>
                                values.includes(opt?.label ?? '') ||
                                values.includes(String(opt.value))
                            )
                            .map((opt) => String(opt.value));
                          handleCustomFilterChange(filter.key, mapped);
                        }}
                      />
                    );
                  })}
                </>
              )}

              {selectedFields.map((field) => (
                <>
                  {!!field.key && (
                    <TemplateFieldFilter
                      key={field.key}
                      field={field}
                      location={location}
                      selected={selectedValues[field.key] || []}
                      handleFilterValuesChange={handleFilterValuesChange(field.key)}
                    />
                  )}
                </>
              ))}

              {toggleFilters.map((toggle) => (
                <Box key={toggle.label}>
                  <Stack>
                    <FilterFormLabel>{toggle.label}</FilterFormLabel>
                    <Switch
                      checked={toggle.checked}
                      onChange={(e) => toggle.onChange(e.target.checked)}
                      size="small"
                      sx={{ marginTop: '4px', marginBottom: '4px' }}
                    />
                  </Stack>
                </Box>
              ))}

              {!hideStaticFilters && (
                <TemplateFieldFilter
                  field={{ name: 'Deck', options: deckOptions }}
                  selected={selectedValues['deck'] || []}
                  handleFilterValuesChange={handleDeckChange}
                  location={location}
                />
              )}
              {validTemplateFields.length > 3 && (
                <AddFilterMenu
                  items={validTemplateFields.slice(4)}
                  selected={selectedFields}
                  setSelected={setSelectedFields}
                  staticFilters={staticFilters}
                />
              )}
            </Stack>
          </AccordionDetails>
        </FilterAccordian>
      )}
    </Box>
  );
};
